import type {
  EditVehicleExpirationPromptResolve,
  EditVehicleServicePlanPromptResolve,
  VehicleExpirationNode,
} from '@/modules/vehicles'
import { EditVehicleExpirationModal } from '@/modules/vehicles/fleet/components/EditVehicleExpirationModal'
import { EditVehicleServicePlanModal } from '@/modules/vehicles/fleet/components/edit-vehicle-service-plan-modal'
import {
  useCreateWorkshopByName,
  useVehicleExpirationMutations,
  useVehicleServicePlanConversionMutations,
  useVehicleServicePlanMutations,
} from '@/modules/vehicles/fleet/mutations.hooks'
import { format } from '@/utils/date-fns'
import { useConfirm } from '@/utils/hooks'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  getVehicleExpirationTypeName,
  getVehicleServicePlanInput,
} from '../util'
import { getVehicleExpirationInput } from './expiration-input'

/**
 * Edit/delete/move/create-service handlers shared by the vehicle expiration dates page and the
 * expirations tab of a single vehicle, plus the add handler only the page needs
 * -- the tab has a vehicle of its own and supplies it itself.
 */
export function useVehicleExpirationHandlers() {
  const translations = useTranslate({
    delete: 'vehicles.prompts.delete-expiration-date',
    deleteConfirmation: [
      'documents.prompts.delete-document-confirmation',
      { name: '' },
    ],
    moveToServices: 'vehicles.expirations.move-to-services',
    moveConfirmation: [
      'vehicles.prompts.move-to-services-confirmation',
      { name: '' },
    ],
    serviceCreated: 'vehicles.expirations.service-created',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const addToast = useToast()

  const { createExpiration, patchExpiration, deleteExpiration } =
    useVehicleExpirationMutations()
  const { convertExpirationToServicePlan } =
    useVehicleServicePlanConversionMutations()
  const { createServicePlan } = useVehicleServicePlanMutations()
  const createWorkshopByName = useCreateWorkshopByName()

  async function addExpiration() {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => <EditVehicleExpirationModal askForVehicle onSubmit={resolve} />
    )
    if (!data) return

    await createExpiration({
      variables: {
        input: {
          ...getVehicleExpirationInput(data.form, data.hasNotification),
          // Asked for in the modal here: unlike the per-vehicle tab, this page
          // has no vehicle of its own to attach the expiration to.
          vehicle: data.form.vehicle ?? undefined,
        },
      },
    })
  }

  async function editExpiration(expiration: VehicleExpirationNode) {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => (
        <EditVehicleExpirationModal
          vehicleExpiration={expiration}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    await patchExpiration({
      variables: {
        id: expiration.id,
        input: getVehicleExpirationInput(data.form, data.hasNotification),
      },
    })
  }

  async function removeExpiration(expiration: VehicleExpirationNode) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: describe(expiration) }),
      translations.delete
    )
    if (!answer) return

    await deleteExpiration({ variables: { id: expiration.id } })
  }

  /**
   * Move a row the FD-571 migration filed as an expiration onto the services
   * page. The migration classified by provenance, which is a guess about intent
   * -- a workshop visit booked under a built-in type looks like a deadline.
   */
  async function moveToServices(expiration: VehicleExpirationNode) {
    const { data: answer } = await confirm(
      translations.moveConfirmation({ name: describe(expiration) }),
      translations.moveToServices
    )
    if (!answer) return

    await convertExpirationToServicePlan({ variables: { id: expiration.id } })
  }

  /** Book a service ahead of an expiration. The expiration itself is kept. */
  async function createServiceFromExpiration(
    expiration: VehicleExpirationNode
  ) {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal
          expiration={expiration}
          askForVehicle={!expiration.vehicle}
          onCreateWorkshop={createWorkshopByName}
          onSubmit={resolve}
        />
      ))
    if (!data) return

    const { data: result } = await createServicePlan({
      variables: {
        input: {
          ...getVehicleServicePlanInput(data.form, data.hasNotification),
          vehicle: data.form.vehicle ?? undefined,
        },
      },
    })
    if (result?.createVehicleServicePlan?.vehicleServicePlan) {
      addToast('success', translations.serviceCreated)
    }
  }

  return {
    addExpiration,
    editExpiration,
    removeExpiration,
    moveToServices,
    createServiceFromExpiration,
  }
}

function describe(expiration: VehicleExpirationNode) {
  return `${format(
    new Date(expiration.expirationDate),
    'PP'
  )} - ${getVehicleExpirationTypeName({ expiration })}`
}
